import { makeAutoObservable, runInAction } from "mobx";
import { AxiosError } from "axios";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { TEAMS_DIGEST_REFUSAL_CODES } from "@/types/msteams-link";
import type { RootStore } from "./root.store";
import type {
  TTeamsDigestRefusalCode,
  TTeamsDigestRefused,
  TTeamsDigestSendResult,
  TTeamsDigestSent,
  TTeamsWorkspaceLink,
  TTeamsSelfLinks,
} from "@/types/msteams-link";

/** Which of this person's round trips is in flight (plan §5a FR5–FR7, NG-1134;
 *  `send_digest` is NG-1641).
 *
 *  A single value rather than a set of booleans, the way
 *  `WorkspaceTeamsConnectionStore` does it: the endpoints all act on one self-scoped
 *  resource, so two of these cannot sensibly overlap and a record of booleans would
 *  let the UI claim they do. */
export type TTeamsLinkPendingAction = "signin" | "unlink" | "send_digest" | null;

/** **The one definition of "connected"** for a person's Teams link, so that nothing
 *  else has to spell it out.
 *
 *  `matched` and nothing more: a matched link is what a Microsoft sign-in produces, and
 *  it stays connected whether or not the Teams app is installed and whether or not the
 *  admin has paused the channel — in both of those the person has done everything they
 *  can, which is why `receiving` is the wrong test and always was. The other four
 *  states (`unlinked`, `uninstalled`, `blocked`, `unmatched`) are not.
 *
 *  Exported, and a function rather than a comparison spelled out per site, because the
 *  word is spent in five places that must agree: the row's *Connected* badge, the
 *  caption counting the workspaces they are also connected in, the Connect button's
 *  offer (negated), the install line (connected, but nothing to deliver to) and the
 *  nudge's list of workspaces messaging their colleagues and not them. Every one of
 *  them said it in its own words before NG-1622, which is five chances for the UI to
 *  use one word for two different facts.
 */
export function isConnected(row: TTeamsWorkspaceLink): boolean {
  return row.link?.state === "matched";
}

/** The caller's own Microsoft Teams links, across every workspace they are in
 *  (plan §5a FR5–FR7, NG-1134, epic NG-886).
 *
 *  **A set, not a singular row** — that is the whole difference from
 *  `WorkspaceTeamsConnectionStore`. A person can be in several workspaces that have
 *  each consented to Teams, so this holds one row per connected workspace and the
 *  actions take no id: the sign-in binds the Microsoft identity to *every* workspace
 *  of theirs on that tenant at once, and unlink opts them out of *all* of them (FR7).
 *  Per-workspace unlink is deliberately out of scope for NG-1134.
 *
 *  **Both writes wait for the server**, which is the design rule's documented
 *  exception rather than a lapse:
 *
 *  * `startSignIn` cannot be optimistic at all — it has no local effect. It mints a
 *    URL and the caller navigates away; what comes back is decided by Microsoft and
 *    then by the callback, so there is no state to apply early.
 *  * `unlink` is **destructive**: every link becomes `unlinked` and pending
 *    deliveries are dropped, and the resulting shape is not derivable here anyway —
 *    the server answers with the whole payload, including workspaces whose
 *    connections were paused meanwhile.
 *
 *  **`sendDigestNow` is neither** (NG-1641): it changes no link, it asks for a card
 *  to be delivered, so it applies nothing on the way back and owns only
 *  `pendingAction`. It lives here rather than being called straight from the block
 *  because that flag is what disables the control for the round trip, and the flag
 *  belongs to whichever call is out.
 *
 *  Nothing in here touches `window`. `startSignIn` returns the URL and the block
 *  navigates, so the redirect is visible at the call site rather than buried in a
 *  mutation, and the store stays testable in jsdom.
 */
export class UserTeamsLinkStore {
  /** The whole payload, or `null` before the first read resolves. `null` and an empty
   *  `workspaces` are different facts and the block renders both as nothing: unknown
   *  is not the same as "no workspace of yours has Teams", and neither may render as
   *  an offer to connect. */
  payload: TTeamsSelfLinks | null = null;
  isLoading = false;
  pendingAction: TTeamsLinkPendingAction = null;

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as never, { autoBind: true });
  }

  /** The connected workspaces, in the server's order (by name). Empty before the
   *  first read, so a caller can map without a null check and still renders nothing. */
  get workspaces(): TTeamsWorkspaceLink[] {
    return this.payload?.workspaces ?? [];
  }

  /** Whether this person has anywhere to link at all — the gate on rendering the
   *  block. False while the read is in flight and false for somebody whose workspaces
   *  have never enabled Teams, which are the two cases that must show nothing. */
  get hasConnectedWorkspaces(): boolean {
    return this.workspaces.length > 0;
  }

  /** Whether any of their links is actually receiving. What the block's summary line
   *  reads, so "connected" never means "matched but nothing installed". */
  get isReceivingAnywhere(): boolean {
    return this.workspaces.some((row) => row.receiving);
  }

  /** The workspaces they are {@link isConnected connected} in — where their Microsoft
   *  identity is bound, whatever the app and the admin have done since.
   *
   *  Empty before the read resolves, like its siblings. */
  get connectedWorkspaces(): TTeamsWorkspaceLink[] {
    return this.workspaces.filter(isConnected);
  }

  /** The ones that are **not the workspace they are looking at** (NG-1622).
   *
   *  What the profile block's caption counts. It lives here rather than in the block
   *  because "connected" is this store's word: the block used to re-derive it from
   *  `link.state` itself, which is one edit away from the caption and the badge beside
   *  it meaning two different things.
   *
   *  Empty where there is no current workspace to be other *than* — an unresolved
   *  router slug, or a workspace list that has not landed. Saying "also connected in 3
   *  others" when we cannot say which one is *this* one would be counting a set whose
   *  complement we do not know.
   */
  get otherConnectedWorkspaces(): TTeamsWorkspaceLink[] {
    const currentId = this.rootStore.workspace.currentWorkspace?.id;
    if (currentId === undefined) return [];
    return this.connectedWorkspaces.filter((row) => row.workspace.id !== currentId);
  }

  /** Whether **this person's own Microsoft identity is bound** anywhere (NG-1187).
   *
   *  The third of the three questions this store answers, and the distinction matters
   *  because {@link hasConnectedWorkspaces} is a fact about their *workspaces* while
   *  this one is a fact about *them*. The block gates on the former — somebody in a
   *  workspace with a live channel and no link of their own is exactly who the block
   *  exists for, and it shows them "Not connected" and a Connect button. The
   *  per-category Teams switches gate on this one instead: a person with no matched
   *  link cannot receive a Teams message whatever they switch, so offering them a
   *  column of switches is offering a control with nothing behind it.
   *
   *  **Matched is enough; installed is not required.** A matched link with no Teams
   *  install is somebody who has proved who they are and not yet added the app, and
   *  choosing categories before installing is a reasonable thing to do — the switches
   *  are settings, not deliveries. `receiving` would have been the wrong test for the
   *  same reason it is the wrong test for the row badge: it is also false for a
   *  workspace whose admin paused the channel, where the person has done everything
   *  they can.
   *
   *  False before the read resolves, like its siblings, so the column appears when the
   *  payload lands rather than flashing an unusable one first.
   */
  get hasMatchedLink(): boolean {
    return this.connectedWorkspaces.length > 0;
  }

  /** The workspaces worth nudging about (OQ2): the channel is **live** there and this
   *  person's Microsoft account is not bound to it, so Teams messages are going to
   *  their colleagues and not to them.
   *
   *  `connection_state === "enabled"` rather than merely present, because a paused or
   *  pending connection sends nothing to anybody and nudging about it would be asking
   *  somebody to fix a problem that is not theirs. And `!isConnected` rather than
   *  `!receiving`, because a connected link that is simply not installed needs the
   *  Teams app, not another trip through Microsoft — the block says so on its row, and
   *  a nudge whose action is the wrong one is worse than no nudge.
   */
  get workspacesWorthNudging(): TTeamsWorkspaceLink[] {
    return this.workspaces.filter((row) => row.connection_state === "enabled" && !isConnected(row));
  }

  /** Read the links unless they are already here or already on the way.
   *
   *  Exists because two independent surfaces want them — the profile block on mount
   *  and the notification bell when it opens — and neither knows about the other. A
   *  bare `fetch()` from each would double the request every time somebody opens the
   *  bell on their own notifications page. Deliberately does **not** re-read: staleness
   *  here is one workspace's badge being a state behind, and the callback landing and
   *  `unlink` both replace the payload outright.
   */
  async ensureFetched(): Promise<void> {
    if (this.payload !== null || this.isLoading) return;
    await this.fetch().catch(() => {
      // The store warned. Callers of this one have no recourse beyond rendering
      // nothing, which is what an absent payload already makes them do.
    });
  }

  async fetch(): Promise<TTeamsSelfLinks | null> {
    this.isLoading = true;
    try {
      const payload = await this.rootStore.msTeamsLinkService.getLinks();
      runInAction(() => {
        this.payload = payload;
      });
      return payload;
    } catch (error) {
      // Warn, not error: the block renders nothing and the notifications page it sits
      // above is fully usable, so this is a degraded read rather than a broken action.
      console.warn("[msteams] failed to read my Teams links", { error });
      throw error;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  /** Mint the Microsoft authorization URL for **this person's** link.
   *
   *  `returnPath` is where the callback comes back to; pass the notifications route so
   *  they land on the block that sent them and see the result. */
  async startSignIn(returnPath: string, source: AnalyticsSource = "page"): Promise<string> {
    this.pendingAction = "signin";
    try {
      const { authorization_url } = await this.rootStore.msTeamsLinkService.startSignIn(returnPath);
      track("teams_link_signin_started", { source });
      return authorization_url;
    } catch (error) {
      console.error("[msteams] failed to start my Microsoft sign-in", { returnPath, error });
      throw error;
    } finally {
      runInAction(() => {
        this.pendingAction = null;
      });
    }
  }

  /** Opt out everywhere. Waits — see the class docstring for why. The response is the
   *  full payload after the change, so it replaces the view rather than being merged
   *  into it, and a workspace paused meanwhile shows up correctly. */
  async unlink(source: AnalyticsSource = "page"): Promise<void> {
    this.pendingAction = "unlink";
    try {
      const payload = await this.rootStore.msTeamsLinkService.unlink();
      runInAction(() => {
        this.payload = payload;
      });
      track("teams_link_removed", { source });
    } catch (error) {
      console.error("[msteams] failed to unlink my Teams account", { error });
      throw error;
    } finally {
      runInAction(() => {
        this.pendingAction = null;
      });
    }
  }

  /** Send this person their Monday summary now, for one workspace (NG-1641).
   *
   *  **Applies nothing.** Unlike its two neighbours it is not a change to the link
   *  set at all — it asks for a card to be delivered — so there is no payload to
   *  replace and the rows on screen are as true afterwards as they were before. All
   *  it owns is `pendingAction`, so the button that started it can say it is working
   *  while the round trip is out.
   *
   *  Resolves with either success shape and rethrows every refusal: the caller is the
   *  only place that knows which sentence a code should become, and swallowing one
   *  here would leave a button that says nothing happened when the server said
   *  exactly why.
   */
  async sendDigestNow(
    workspaceSlug: string,
    source: AnalyticsSource = "page",
  ): Promise<TTeamsDigestSendResult> {
    this.pendingAction = "send_digest";
    try {
      const result = await this.rootStore.msTeamsLinkService.sendDigestNow(workspaceSlug);
      // `sent` is false when there was nothing to summarise, which still answers the ask.
      track("teams_digest_sent", { sent: result.sent, source });
      return result;
    } catch (error) {
      console.error("[msteams] failed to send my Monday summary now", { workspaceSlug, error });
      throw error;
    } finally {
      runInAction(() => {
        this.pendingAction = null;
      });
    }
  }

  reset(): void {
    this.payload = null;
    this.isLoading = false;
    this.pendingAction = null;
  }
}

/** Whether a resolved send actually sent a card, narrowing the union as it goes.
 *
 *  `sent` is the wire's own discriminator — always `true` on the 202 and always
 *  `false` on the 200 — but the generated type widens both to `boolean`, so the
 *  narrowing has to be stated once here rather than being a property access the
 *  compiler understands. One definition, because the alternative is each call site
 *  reaching for `"delivery_id" in result` and two of them disagreeing about what
 *  "sent" means.
 */
export function isDigestSent(result: TTeamsDigestSendResult): result is TTeamsDigestSent {
  return result.sent;
}

/** The refusal code behind a failed send, or `null` for anything we have no sentence
 *  for — a network failure, a 500, or a code this build has never heard of.
 *
 *  The same shape as `teamsErrorCode` beside it and for the same reason: the value
 *  comes off the wire and becomes half of an i18n key, so it is matched against a
 *  closed set rather than trusted. A `null` is not a missing answer, it is the
 *  answer — it means the generic failure copy, which is the honest thing to say
 *  about a reason we cannot name.
 */
export function digestRefusalCode(error: unknown): TTeamsDigestRefusalCode | null {
  const data = (error as AxiosError<TTeamsDigestRefused>)?.response?.data;
  return TEAMS_DIGEST_REFUSAL_CODES.find((known) => known === data?.code) ?? null;
}
